/**
 * VoiceInput Component
 *
 * A microphone button component for speech-to-text input.
 * Provides visual feedback during recording and can be integrated
 * with any text input field.
 *
 * Features:
 * - Animated recording indicator
 * - Real-time transcript display
 * - Error handling with user feedback
 * - Customizable styling via className
 * - Accessible with ARIA labels
 *
 * @example
 * <VoiceInput
 *   onTranscript={(text) => setInputValue(text)}
 *   onFinalTranscript={(text) => handleSubmit(text)}
 *   placeholder="Click to speak..."
 * />
 */

import React, { useState, useCallback } from 'react';
import { FaMicrophone, FaStop, FaMicrophoneSlash } from 'react-icons/fa';
import useSpeechRecognition from '../../hooks/useSpeechRecognition';

/**
 * VoiceInput component for speech-to-text
 * @param {Object} props - Component props
 * @param {function} props.onTranscript - Called with interim transcript while speaking
 * @param {function} props.onFinalTranscript - Called with final transcript when done
 * @param {function} props.onListeningChange - Called when listening state changes
 * @param {string} props.language - Language code (default: 'en-US')
 * @param {boolean} props.continuous - Keep listening after speech ends
 * @param {boolean} props.showTranscript - Show transcript preview below button
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.buttonClassName - CSS classes for button
 * @param {string} props.size - Button size: 'sm', 'md', 'lg' (default: 'md')
 * @param {boolean} props.disabled - Disable the button
 * @param {string} props.tooltip - Custom tooltip text
 */
const VoiceInput = ({
  onTranscript,
  onFinalTranscript,
  onListeningChange,
  language = 'en-US',
  continuous = false,
  showTranscript = false,
  className = '',
  buttonClassName = '',
  size = 'md',
  disabled = false,
  tooltip,
}) => {
  const [showError, setShowError] = useState(false);

  const handleResult = useCallback(
    (text, fullTranscript) => {
      if (onFinalTranscript) {
        onFinalTranscript(fullTranscript);
      }
    },
    [onFinalTranscript]
  );

  const handleError = useCallback((errorCode, errorMessage) => {
    setShowError(true);
    setTimeout(() => setShowError(false), 3000);
  }, []);

  const {
    transcript,
    interimTranscript,
    isListening,
    error,
    browserSupported,
    startListening,
    stopListening,
    resetTranscript,
  } = useSpeechRecognition({
    language,
    continuous,
    interimResults: true,
    onResult: handleResult,
    onError: handleError,
  });

  // Notify parent of transcript changes
  React.useEffect(() => {
    if (onTranscript && (transcript || interimTranscript)) {
      onTranscript(transcript + interimTranscript);
    }
  }, [transcript, interimTranscript, onTranscript]);

  // Notify parent of listening state changes
  React.useEffect(() => {
    if (onListeningChange) {
      onListeningChange(isListening);
    }
  }, [isListening, onListeningChange]);

  const handleClick = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      resetTranscript();
      startListening();
    }
  }, [isListening, startListening, stopListening, resetTranscript]);

  // Size classes
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
  };

  // If browser doesn't support speech recognition, don't render
  if (!browserSupported) {
    return null;
  }

  const buttonTitle =
    tooltip ||
    (isListening
      ? 'Click to stop recording'
      : error
      ? error
      : 'Click to start voice input');

  return (
    <div className={`voice-input-container inline-flex flex-col items-center ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={buttonTitle}
        aria-label={isListening ? 'Stop recording' : 'Start voice input'}
        aria-pressed={isListening}
        className={`
          relative rounded-full flex items-center justify-center
          transition-all duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-offset-2
          ${sizeClasses[size]}
          ${
            disabled
              ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
              : isListening
              ? 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500'
              : 'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500'
          }
          ${buttonClassName}
        `}
      >
        {/* Pulsing animation when listening */}
        {isListening && (
          <>
            <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-75" />
            <span className="absolute inset-0 rounded-full bg-red-500 animate-pulse" />
          </>
        )}

        {/* Icon */}
        <span className="relative z-10">
          {disabled ? (
            <FaMicrophoneSlash />
          ) : isListening ? (
            <FaStop />
          ) : (
            <FaMicrophone />
          )}
        </span>
      </button>

      {/* Error message */}
      {showError && error && (
        <div className="mt-2 text-xs text-red-500 text-center max-w-[200px]">
          {error}
        </div>
      )}

      {/* Transcript preview */}
      {showTranscript && (transcript || interimTranscript) && (
        <div className="mt-2 p-2 bg-neutral-100 rounded-lg text-sm max-w-[250px]">
          <span className="text-neutral-800">{transcript}</span>
          {interimTranscript && (
            <span className="text-neutral-400 italic">{interimTranscript}</span>
          )}
        </div>
      )}

      {/* Listening indicator text */}
      {isListening && !showTranscript && (
        <div className="mt-1 text-xs text-red-500 animate-pulse">
          Listening...
        </div>
      )}
    </div>
  );
};

/**
 * VoiceInputInline - A compact inline version for text inputs
 * Can be placed inside or next to an input field
 */
export const VoiceInputInline = ({
  onTranscript,
  onListeningChange,
  language = 'en-US',
  disabled = false,
  className = '',
}) => {
  const { isListening, browserSupported, toggleListening, transcript, interimTranscript } =
    useSpeechRecognition({
      language,
      continuous: false,
      interimResults: true,
    });

  React.useEffect(() => {
    if (onTranscript && (transcript || interimTranscript)) {
      onTranscript(transcript + interimTranscript);
    }
  }, [transcript, interimTranscript, onTranscript]);

  // Notify parent of listening state changes
  React.useEffect(() => {
    if (onListeningChange) {
      onListeningChange(isListening);
    }
  }, [isListening, onListeningChange]);

  if (!browserSupported) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={toggleListening}
      disabled={disabled}
      aria-label={isListening ? 'Stop recording' : 'Start voice input'}
      className={`
        p-2 rounded-full transition-colors duration-200
        ${
          isListening
            ? 'text-red-500 bg-red-50 hover:bg-red-100'
            : 'text-neutral-400 hover:text-primary-500 hover:bg-primary-50'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
    >
      {isListening ? (
        <FaStop className="w-4 h-4" />
      ) : (
        <FaMicrophone className="w-4 h-4" />
      )}
    </button>
  );
};

export default VoiceInput;
